import {
  createCodeableConcept,
  createReference,
  isValidReference,
  mapFhirToModelPeriod,
  type WithId,
} from '@quromedical/fhir-common';
import { compact, first } from '@quromedical/utils';
import type { CareTeamParticipant, CodeableConcept } from 'fhir/r4';

import type {
  CareTeam,
  CareTeamMembers,
  CreateCareTeam,
  UpdateCareTeam,
} from './schema';

export const mapCreateModelToFhirCareTeam = (
  careTeam: CreateCareTeam,
): fhir4.CareTeam => {
  const {
    name,
    status,
    period,
    managingOrganization,
    patient,
    admission,
    teamMembers,
  } = careTeam;

  const organizations = compact(
    managingOrganization?.map((organization) =>
      createReference('Organization', organization),
    ),
  );
  const subject = patient ? createReference('Patient', patient) : undefined;
  const encounter = admission
    ? createReference('Encounter', admission)
    : undefined;

  const participant = compact(
    teamMembers.map<CareTeamParticipant>((team) => {
      return {
        role: team.role.map((code) => createCodeableConcept(code)),
        member: createReference('Practitioner', team.member),
      };
    }),
  );

  return {
    resourceType: 'CareTeam',
    name,
    status,
    period,
    managingOrganization: organizations,
    subject,
    encounter,
    participant,
  };
};

export const mapUpdateModelToFhirCareTeam = (
  careTeam: UpdateCareTeam,
): WithId<fhir4.CareTeam> => {
  const { id } = careTeam;

  const careTeams = mapCreateModelToFhirCareTeam(careTeam);
  return {
    id,
    ...careTeams,
  };
};

export const mapModelToFhirCareTeam = (careTeam: CareTeam): fhir4.CareTeam => {
  const {
    id,
    status,
    name,
    period,
    managingOrganization,
    patient,
    admission,
    teamMembers,
  } = careTeam;

  return {
    id,
    resourceType: 'CareTeam',
    name,
    status,
    period,
    managingOrganization,
    subject: patient,
    encounter: admission,
    participant: teamMembers,
  };
};
const getCodeableConceptCode = (code?: CodeableConcept) => {
  const coding = code ? first(code.coding) : undefined;
  const codes = coding ? coding.code : undefined;
  const display = coding ? coding.display : undefined;
  const codeableConceptCode = codes
    ? createCodeableConcept(codes, codes, display)
    : undefined;

  return codeableConceptCode;
};
export const mapFhirCareTeamToModel = (
  fhir: WithId<fhir4.CareTeam>,
): Partial<CareTeam> => {
  const {
    id,
    name,
    status,
    period,
    managingOrganization,
    subject,
    encounter,
    participant,
  } = fhir;
  const organizations = compact(
    managingOrganization?.map((organization) =>
      isValidReference(organization) ? organization : undefined,
    ),
  );
  const subjectReference = isValidReference(subject) ? subject : undefined;
  const encounterReference = isValidReference(encounter)
    ? encounter
    : undefined;

  const teamMembers = compact(
    participant?.map<CareTeamMembers | undefined>((team) => {
      const member = isValidReference(team.member) ? team.member : undefined;

      if (!member) {
        return undefined;
      }

      return {
        role: compact(team.role?.map(getCodeableConceptCode)),
        member,
      };
    }),
  );
  return {
    id,
    name,
    status,
    period: mapFhirToModelPeriod(period),
    managingOrganization: organizations,
    patient: subjectReference,
    admission: encounterReference,
    teamMembers,
  };
};
